import React from "react";
import {
  Container,
  Paper,
  Button,
  Typography,
  IconButton,
  Grid,
  TextField,
  FormControlLabel,
  InputAdornment,
  Checkbox,
  FormGroup
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import swal from "sweetalert";
import * as companyApi from "../../api/companyApi";
import ScrollToTopBtn from "../common/ScrollToTop";
import { withRouter } from "../common/WithRouter";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

class CompanyList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      companies: [],
      searchQuery: "",
      showTFF: false,
      showYFF: false,
    };
  }

  async componentDidMount() {
    try {
      const companies = await companyApi.getCompanies();
      if (companies) {
        companies.sort((a, b) => a.companyName.localeCompare(b.companyName));
        this.setState({ companies });
      } else {
        this.setState({ companies: [] });
      }
    } catch (error) {
      console.error("Error in componentDidMount", error);
    }
  }

  showColor = (value) => {
    switch (value) {
      case "Aktiv":
        return { color: "#00c853" };
      case "Inaktiv":
        return { color: "#9e9e9e" };
      case "Ikke medlem":
        return { color: "#ff8a80" };
      default:
        return {};
    }
  };

  handleDeleteCompany = (company) => {
    if (company.tffStatusName === "Ikke medlem" && company.yffStatusName === "Ikke medlem") {
      swal({
        title: "Slette bedrift?",
        text: company.companyName,
        icon: "warning",
        buttons: ["Avbryt", "OK"],
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          try {
            const response = await companyApi.deleteCompany(company.companyId);
            if (response) {
              this.setState((prevState) => ({
                companies: prevState.companies.filter((c) => c.companyId !== company.companyId),
              }));
              swal("Bedrift er slettet!", { icon: "success" });
            } else {
              swal("Det oppsto en feil...", { icon: "error" });
            }
          } catch (error) {
            console.error(error);
          }
        }
      });
    } else {
      swal({
        title: "Bedrift kan IKKE slettes!",
        text: "Bedrift er medlem i TFF/YFF",
        icon: "warning",
        dangerMode: true,
      });
    }
  };

  handledownload = async () => {
    const { companies, showTFF, showYFF } = this.state;

    // Filter active companies based on current checkboxes
    const activeCompanies = companies.filter((company) => {
      const tffActive = company.tffStatusName === "Aktiv";
      const yffActive = company.yffStatusName === "Aktiv";
      return (
        (!showTFF && !showYFF) ||
        (showTFF && tffActive) ||
        (showYFF && yffActive) ||
        (showTFF && showYFF && (tffActive || yffActive))
      );
    });

    if (activeCompanies.length === 0) {
      swal({
        title: "Ingen aktive bedrifter funnet!",
        text: "Det finnes ingen bedrifter som matcher filteret ditt.",
        icon: "warning",
      });
      return;
    }

    try {
      const exportData = await Promise.all(
        activeCompanies.map(async (company) => {
          const details = await companyApi.getCompany(company.companyId);
          console.log(details)
          return {
            Bedriftsnavn: company.companyName,
            TFF_Status: company.tffStatusName,
            YFF_Status: company.yffStatusName,
            OrgNr: details.orgnr || "",
            Epost: details.emailCompany || "",
            Telefon: details.phonenumber || "",
          };
        })
      );

      // Generate Excel
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "AktiveBedrifter");

      // Write and save
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/octet-stream" });

      saveAs(data, "aktive_bedrifter.xlsx");
    } catch (error) {
      console.error("Feil ved henting av firmadetaljer:", error);
      swal({
        title: "Feil oppstod",
        text: "Kunne ikke hente all informasjon. Prøv igjen senere.",
        icon: "error",
      });
    }
  };

  handleSearch = (event) => {
    this.setState({ searchQuery: event.target.value });
  };

  handleFilterChange = (event) => {
    this.setState({ [event.target.name]: event.target.checked });
  };


  render() {
    const columns = [
      {
        field: "actions",
        headerName: "",
        width: 160,
        sortable: false,
        resizable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="medium"
            color="primary"
            onClick={() => this.props.navigate(`/companydetail/${params.row.companyId}`)}
          >
            <ArrowForwardIosIcon sx={{ mr: 1 }} />
            Vis Mer
          </Button>
        ),
      },
      {
        field: "companyName",
        headerName: "Bedriftsnavn",
        flex: 2,
        sortable: true,
        resizable: false,
      },
      {
        field: "tffStatusName",
        headerName: "TFF",
        flex: 1,
        sortable: true,
        resizable: false,
        renderCell: (params) => (
          <Typography style={this.showColor(params.value)}>{params.value}</Typography>
        ),
      },
      {
        field: "yffStatusName",
        headerName: "YFF",
        flex: 1,
        sortable: true,
        resizable: false,
        renderCell: (params) => (
          <Typography style={this.showColor(params.value)}>{params.value}</Typography>
        ),
      },
      {
        field: "delete",
        headerName: "",
        width: 100,
        sortable: false,
        resizable: false,
        renderCell: (params) => (
          <IconButton color="error" onClick={() => this.handleDeleteCompany(params.row)}>
            <DeleteForeverIcon />
          </IconButton>
        ),
      },
    ];

    const { companies, searchQuery, showTFF, showYFF } = this.state;

    const filteredCompanies = companies.filter((company) => {
      const matchesSearch = company.companyName.toLowerCase().includes(searchQuery.toLowerCase());

      const tffIsActive = company.tffStatusName === "Aktiv";
      const yffIsActive = company.yffStatusName === "Aktiv";

      const matchesFilter =
        (!showTFF && !showYFF) ||
        (showTFF && tffIsActive) ||
        (showYFF && yffIsActive) ||
        (showTFF && showYFF && (tffIsActive || yffIsActive));

      return matchesSearch && matchesFilter;
    });

    return (
      <React.Fragment>
        <Container maxWidth="lg">
          <main style={{ width: "100%", paddingTop: 80 }}>
            <Paper sx={{ width: "100%", p: 3 }}>
              {/* Title */}
              <Typography variant="h5" sx={{ mb: 2 }}>
                Bedriftsoversikt
              </Typography>

              {/* Search and Filters */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>

                {/* Search Field */}
                <Grid item>
                  <TextField
                    placeholder="Søk"
                    variant="standard"
                    sx={{ width: "250px", backgroundColor: "white", borderRadius: 1 }}
                    value={searchQuery}
                    onChange={this.handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* TFF/YFF Filters */}
                <Grid item>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showTFF}
                          onChange={this.handleFilterChange}
                          name="showTFF"
                        />
                      }
                      label="Vis aktive TFF"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showYFF}
                          onChange={this.handleFilterChange}
                          name="showYFF"
                        />
                      }
                      label="Vis aktive YFF"
                    />
                  </FormGroup>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudDownloadIcon />}
                    onClick={this.handledownload}
                  >
                    Vis i Excel
                  </Button>
                </Grid>
              </Grid>

              {/* Data Grid Table */}
              <DataGrid
                rows={filteredCompanies}
                columns={columns}
                pageSizeOptions={[10, 50, 100]}
                pagination
                autoHeight
                disableSelectionOnClick
                getRowId={(row) => row.companyId}
              />
            </Paper>
          </main>
        </Container>
        <ScrollToTopBtn />
      </React.Fragment>
    );
  }
}

export default withRouter(CompanyList);
